import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import IconButton from 'LuxKit/Button/IconButton'
import Divider from 'LuxKit/Divider'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import getLuxPlusTotalValueSaved from 'luxPlus/selectors/benefits/getLuxPlusTotalValueSaved'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div`
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.M};
`

const FadedCaption = styled(Caption)`
  opacity: 0.7;
`

const AccountSnapshotContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
`

const FadedDivider = styled(Divider)`
  opacity: 0.3;
`

const FadedLoadingBox = styled(LoadingBox)`
  opacity: 0.3;
`

interface Props {
  onLinkClick: () => void
}

function AccountMenuLuxPlusDetailsTile({ onLinkClick }: Props) {
  const intl = useIntl()
  const totalValueSaved = useAppSelector(getLuxPlusTotalValueSaved)
  const luxPlusSubscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const isSubscribedLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)

  if (luxPlusSubscription.fetching) {
    return <Container>
      <Group direction="vertical" gap={12}>
        <div>
          <LuxPlusLogo colour="inherit" height={12} />
        </div>
        <div>
          <FadedLoadingBox height={49} />
        </div>
      </Group>
    </Container>
  }

  if (!isSubscribedLuxPlusMember || luxPlusSubscription.error) return null

  return <Container
    as={Clickable}
    to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}
    onClick={onLinkClick}
  >
    <Group direction="vertical" gap={12}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <LuxPlusLogo colour="inherit" height={12} />
        <IconButton
          kind="tertiary"
          variant="ghost"
          horizontalOutdent="end"
          size="small"
          nonInteractable
        >
          <LineAngleRightIcon />
        </IconButton>
      </Group>
      {!totalValueSaved && <FadedCaption variant="medium" align="center">
        {intl.formatMessage(
          {
            id: 'SXLxufGOBF5t99JdjpMR',
            defaultMessage: 'Welcome to {programName} where you’ll save even more',
            description: 'Welcome message for new Luxury Plus members in the navigation bar',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        )}
      </FadedCaption>}
      {!!totalValueSaved && <AccountSnapshotContainer>
        <Group direction="vertical" gap={2} horizontalAlign="center">
          <FadedCaption variant="medium">
            {intl.formatMessage({
              id: '72c_xl5uS1fx5XKBexg8',
              defaultMessage: 'Savings',
              description: 'Form field label for total savings amount in the navigation bar',
            })}
          </FadedCaption>
          <Heading variant="heading6" colour="dark-contrast">
            <FormatCurrency value={totalValueSaved} format="roundedDollar" />
          </Heading>
        </Group>
        <div>
          <FadedDivider kind="primary" direction="vertical" />
        </div>
        <Group direction="vertical" gap={2} horizontalAlign="center">
          <FadedCaption variant="medium">
            {intl.formatMessage({
              id: 'Gqw1r2KnaIdwTEv9zEsP',
              defaultMessage: 'Join date',
              description: 'Label for membership join date in the date selection or calendar picker',
            })}
          </FadedCaption>
          <Heading variant="heading6" colour="dark-contrast">
            <FormatDateTime value={luxPlusSubscription.item!.memberSince} year="2-digit" month="short" day="2-digit" />
          </Heading>
        </Group>
      </AccountSnapshotContainer>}
    </Group>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLuxPlusEnabled)
})(AccountMenuLuxPlusDetailsTile)
